import React, { useCallback, useEffect, useRef, useState } from "react";
import { chatApi } from "./chatApi";
import "./AdvisorChat.css";

const POLL_INTERVAL_MS = 5000;


const OFF_TOPIC_HINTS = [
  { pattern: /\b(job opening|hire me|any vacancies|open position|job for me)\b/i,
    message: "Advisors can't help you find or apply for jobs here — but resume, portfolio, and interview-prep questions are welcome." },
  { pattern: /\b(whatsapp number|phone number|personal number|are you single|how old are you|where do you live)\b/i,
    message: "This chat is for course and career-skill support — let's keep it to that." },
];

function TickIcon({ seen }) {
  return (
    <span className={`advisor-chat__ticks ${seen ? "advisor-chat__ticks--seen" : "advisor-chat__ticks--sent"}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12l5 5L15 8" />
        <path d="M9 12l5 5L23 8" />
      </svg>
    </span>
  );
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function AdvisorChat({ currentUserId, startOpen = false }) {
  const [isOpen, setIsOpen] = useState(startOpen);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [inlineWarning, setInlineWarning] = useState(null);
  const [sending, setSending] = useState(false);
  const [banned, setBanned] = useState(false);
  const [rulesExpanded, setRulesExpanded] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const messagesEndRef = useRef(null);
  const lastMessageIdRef = useRef(0);
  const pollTimerRef = useRef(null);

  // ---- Load / create the conversation on mount ----
  useEffect(() => {
    let cancelled = false;
    chatApi
      .getConversation()
      .then((res) => {
        if (cancelled) return;
        setConversationId(res.data.id);
        setMessages(res.data.messages || []);
        const last = (res.data.messages || []).slice(-1)[0];
        lastMessageIdRef.current = last ? last.id : 0;
      })
      .catch((err) => {
        if (err?.response?.status === 403 && err?.response?.data?.reason === "banned") {
          setBanned(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Polling for new messages ----
  const poll = useCallback(() => {
    if (!conversationId) return;
    chatApi
      .getMessages(conversationId, lastMessageIdRef.current)
      .then((res) => {
        const incoming = res.data.messages || [];
        if (incoming.length === 0) return;
        setMessages((prev) => [...prev, ...incoming]);
        lastMessageIdRef.current = incoming[incoming.length - 1].id;
        if (!isOpen) {
          setUnreadCount((c) => c + incoming.filter((m) => m.sender_type === "advisor").length);
        }
      })
      .catch(() => {
        // Swallow transient polling errors — next tick will retry.
      });
  }, [conversationId, isOpen]);

  useEffect(() => {
    if (!conversationId) return;

    const tick = () => {
      // Pause polling in background tabs to avoid hammering the backend.
      if (document.visibilityState === "visible") poll();
    };

    pollTimerRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(pollTimerRef.current);
  }, [conversationId, poll]);

  // ---- Mark messages seen whenever the panel is open and up to date ----
  useEffect(() => {
    if (!isOpen || !conversationId || messages.length === 0) return;
    const lastId = messages[messages.length - 1].id;
    chatApi.markSeen(conversationId, lastId).catch(() => {});
    setUnreadCount(0);
  }, [isOpen, conversationId, messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen]);

  function checkInlineHint(text) {
    const hit = OFF_TOPIC_HINTS.find((h) => h.pattern.test(text));
    setInlineWarning(hit ? hit.message : null);
  }

  function handleInputChange(e) {
    const value = e.target.value;
    setInput(value);
    checkInlineHint(value);
  }

  function handleSend() {
    const body = input.trim();
    if (!body || !conversationId || sending) return;

    setSending(true);
    chatApi
      .sendMessage(conversationId, body)
      .then((res) => {
        setMessages((prev) => [...prev, res.data]);
        lastMessageIdRef.current = res.data.id;
        setInput("");
        setInlineWarning(null);
      })
      .catch((err) => {
        const data = err?.response?.data;
        if (err?.response?.status === 403 && data?.reason === "insult_detected") {
          setBanned(true);
        } else if (data?.reason) {
          // Backend rejected for an engagement-rule reason (off-topic, job-seeking, etc.)
          setInlineWarning(data.detail || "That message doesn't fit what advisors can help with here.");
        } else {
          setInlineWarning("Couldn't send that message — please try again.");
        }
      })
      .finally(() => setSending(false));
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (banned) {
    return (
      <div className="advisor-chat">
        <div className="advisor-chat__panel" style={{ height: "auto" }}>
          <div className="advisor-chat__header">
            <div>
              <div className="advisor-chat__header-title">Chat with Advisor/Expert</div>
            </div>
          </div>
          <div className="advisor-chat__banned">
            Your access to advisor chat has been permanently suspended for violating the
            engagement guidelines (insults or abusive language toward advisors). This
            decision is final and cannot be appealed through this chat.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="advisor-chat">
      {!isOpen ? (
        <button className="advisor-chat__tab" onClick={() => setIsOpen(true)}>
          <span className="advisor-chat__status-dot" title="Advisors offline" aria-label="Offline" />
          <span className="advisor-chat__tab-title">Chat with CS Professional/Expert</span>
          {unreadCount > 0 && <span className="advisor-chat__unread">{unreadCount}</span>}
        </button>
      ) : (
        <div className="advisor-chat__panel">
          <div className="advisor-chat__header">
            <div>
              <div className="advisor-chat__header-title">
                <span className="advisor-chat__status-dot" title="Advisors offline" aria-label="Offline" />
                Chat with CS Professional/Expert</div>
              <div className="advisor-chat__header-sub">1:1 with a CS Professional</div>
            </div>
            <button className="advisor-chat__close" onClick={() => setIsOpen(false)} aria-label="Minimize">
              &#8595;
            </button>
          </div>

          <div className="advisor-chat__notice">
            Messages will be replied to when an advisor is available. This isn't
            live chat — replies may take a few hours.
          </div>

          <div className="advisor-chat__rules">
            Please keep questions focused on your coursework and career prep.{" "}
            <button onClick={() => setRulesExpanded((v) => !v)}>
              {rulesExpanded ? "Hide guidelines" : "View guidelines"}
            </button>
            {rulesExpanded && (
              <ul className="advisor-chat__rules-list">
                <li>Ask for feedback on resumes, portfolios, or interview prep — advisors won't source or refer you to jobs directly.</li>
                <li>Keep it course- and career-related; this isn't a space for personal conversation.</li>
                <li>No insults or abusive language — this results in a permanent ban from chat.</li>
              </ul>
            )}
          </div>

          <div className="advisor-chat__messages">
            {messages.length === 0 && (
              <div className="advisor-chat__empty">
                Ask your advisor about coursework, projects, or career prep like resume
                and interview feedback.
              </div>
            )}
            {messages.map((m) => {
              if (m.sender_type === "system") {
                return (
                  <div key={m.id} className="advisor-chat__bubble-row" style={{ alignSelf: "center" }}>
                    <div className="advisor-chat__bubble advisor-chat__bubble--system">{m.body}</div>
                  </div>
                );
              }
              const own = m.sender_type === "user";
              return (
                <div
                  key={m.id}
                  className={`advisor-chat__bubble-row ${own ? "advisor-chat__bubble-row--own" : "advisor-chat__bubble-row--advisor"}`}
                >
                  <div className="advisor-chat__bubble">{m.body}</div>
                  <div className="advisor-chat__meta">
                    <span>{formatTime(m.created_at)}</span>
                    {own && <TickIcon seen={m.status === "seen"} />}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <div className="advisor-chat__composer">
            {inlineWarning && <div className="advisor-chat__warning">{inlineWarning}</div>}
            <div className="advisor-chat__input-row">
              <textarea
                className="advisor-chat__textarea"
                placeholder="Message your advisor…"
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                rows={1}
              />
              <button className="advisor-chat__send" onClick={handleSend} disabled={!input.trim() || sending}>
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}